import { FinancialConfig } from '@futrli-sdk/predict';
import { memoize } from 'micro-memoize';

import { NonFinancialDriverType } from '../../../predict/src/core/api/types/non-financial-drivers';
import type {
  FormulaNonFinancialDriver,
  FormulaNonFinancialDriverSaveData,
  FormulaNonFinancialDriverUpdateData,
  FreestyleNonFinancialDriver,
  FreestyleNonFinancialDriverSaveData,
  FreestyleNonFinancialDriverUpdateData,
  NonFinancialDriverFormulaOptionsResponse,
  NonFinancialDriverNodeList
} from '../../../predict/src/core/api/types/non-financial-drivers';
import { ApiView, ApiViewState, getDefaultApiViewState } from '../../core/ApiView';
import {
  createFormulaDriverMutation,
  createFreestyleDriverMutation,
  updateFormulaDriverMutation,
  updateFreestyleDriverMutation
} from '../../mutations/nonFinancialDrivers';
import { financialConfigQuery } from '../../queries/financialConfig';
import {
  driversOrderQuery,
  formulaDriverDetailQuery,
  formulaDriverQueryConfig,
  formulaOptionsQuery,
  freestyleDriverDetailQuery,
  freestyleDriverQueryConfig
} from '../../queries/nonFinancialDrivers';
import { getGroups } from '../../processors/nonFinancialDrivers/groups';
import { DriverTreeNode } from '../../processors/nonFinancialDrivers/driversTree';

export interface CreateNonFinancialDriverViewState extends ApiViewState {
  formulaDriver?: FormulaNonFinancialDriver;
  freestyleDriver?: FreestyleNonFinancialDriver;
  driversOrder?: NonFinancialDriverNodeList[];
  errorCreatingDriver: Error | null;
  errorUpdatingDriver: Error | null;
  financialConfig?: FinancialConfig;
  formulaOptions?: NonFinancialDriverFormulaOptionsResponse;
  groups?: DriverTreeNode[];
  isCreatingDriver: boolean;
  isUpdatingDriver: boolean;
  newDriverId?: string;
  updatedDriverId?: string;
}

export class CreateNonFinancialDriverView extends ApiView {
  private formulaDriverDetailObs: ReturnType<typeof formulaDriverDetailQuery>;
  private freestyleDriverDetailObs: ReturnType<typeof freestyleDriverDetailQuery>;
  private driversOrderObs: ReturnType<typeof driversOrderQuery>;
  private financialConfigObs: ReturnType<typeof financialConfigQuery>;
  private formulaOptionsObs: ReturnType<typeof formulaOptionsQuery>;

  private createFormulaDriverMut: ReturnType<typeof createFormulaDriverMutation>;
  private createFreestyleDriverMut: ReturnType<typeof createFreestyleDriverMutation>;
  private updateFormulaDriverMut: ReturnType<typeof updateFormulaDriverMutation>;
  private updateFreestyleDriverMut: ReturnType<typeof updateFreestyleDriverMutation>;

  private memoizedGroups = memoize(getGroups);

  private driverId?: string;
  private loadedDriverId?: string;

  public state: CreateNonFinancialDriverViewState = {
    ...getDefaultApiViewState(),
    formulaDriver: undefined,
    freestyleDriver: undefined,
    driversOrder: undefined,
    errorCreatingDriver: null,
    errorUpdatingDriver: null,
    financialConfig: undefined,
    formulaOptions: undefined,
    isCreatingDriver: false,
    isUpdatingDriver: false,
    newDriverId: undefined,
    updatedDriverId: undefined
  };

  constructor(private workspaceId: string, private organisationId: string, driverId?: string) {
    super();

    this.driverId = driverId;

    this.formulaDriverDetailObs = this.addQuery(formulaDriverDetailQuery());
    this.freestyleDriverDetailObs = this.addQuery(freestyleDriverDetailQuery());
    this.driversOrderObs = this.addQuery(driversOrderQuery(workspaceId, organisationId));
    this.financialConfigObs = this.addQuery(
      financialConfigQuery({ workspaceId, entityType: 'organisation', entityId: organisationId })
    );
    this.formulaOptionsObs = this.addQuery(formulaOptionsQuery(workspaceId, organisationId));

    this.createFormulaDriverMut = this.addMutation(createFormulaDriverMutation(workspaceId, organisationId));
    this.createFreestyleDriverMut = this.addMutation(createFreestyleDriverMutation(workspaceId, organisationId));
    this.updateFormulaDriverMut = this.addMutation(updateFormulaDriverMutation(workspaceId, organisationId));
    this.updateFreestyleDriverMut = this.addMutation(updateFreestyleDriverMutation(workspaceId, organisationId));

    this.updateState();
  }

  // ***************
  // *  Public API *
  // ***************

  public createFormulaDriver(data: FormulaNonFinancialDriverSaveData) {
    return this.createFormulaDriverMut.mutate(data);
  }

  public createFreestyleDriver(data: FreestyleNonFinancialDriverSaveData) {
    return this.createFreestyleDriverMut.mutate(data);
  }

  public setDriverId(driverId: string) {
    this.driverId = driverId;
    this.notify();
  }

  public updateFormulaDriver(driverId: string, data: FormulaNonFinancialDriverUpdateData) {
    return this.updateFormulaDriverMut.mutate({ driverId, data });
  }

  public updateFreestyleDriver(driverId: string, data: FreestyleNonFinancialDriverUpdateData) {
    return this.updateFreestyleDriverMut.mutate({ driverId, data });
  }

  // ****************************
  // *  Private / Protected API *
  // ****************************

  private get formulaDriver() {
    return this.getResult(this.formulaDriverDetailObs).data;
  }

  private get freestyleDriver() {
    return this.getResult(this.freestyleDriverDetailObs).data;
  }

  private get driversOrder() {
    return this.getResult(this.driversOrderObs).data;
  }

  private get financialConfig() {
    return this.getResult(this.financialConfigObs).data;
  }

  private get formulaOptions() {
    return this.getResult(this.formulaOptionsObs).data;
  }

  private get groups() {
    return this.driversOrder ? this.memoizedGroups(this.driversOrder) : undefined;
  }

  private get errorCreatingDriver() {
    return (
      this.createFormulaDriverMut.getCurrentResult().error ?? this.createFreestyleDriverMut.getCurrentResult().error
    );
  }

  private get errorUpdatingDriver() {
    return (
      this.updateFormulaDriverMut.getCurrentResult().error ?? this.updateFreestyleDriverMut.getCurrentResult().error
    );
  }

  protected getNewState(): CreateNonFinancialDriverViewState {
    return {
      ...super.getNewState(),
      formulaDriver: this.formulaDriver,
      freestyleDriver: this.freestyleDriver,
      driversOrder: this.driversOrder,
      errorCreatingDriver: this.errorCreatingDriver,
      errorUpdatingDriver: this.errorUpdatingDriver,
      financialConfig: this.financialConfig,
      formulaOptions: this.formulaOptions,
      groups: this.groups,
      isCreatingDriver: this.isCreatingDriver,
      isUpdatingDriver: this.isUpdatingDriver,
      newDriverId: this.newDriverId,
      updatedDriverId: this.updatedDriverId
    };
  }

  private get isCreatingDriver() {
    return (
      this.createFormulaDriverMut.getCurrentResult().status === 'pending' ||
      this.createFreestyleDriverMut.getCurrentResult().status === 'pending'
    );
  }

  private get isUpdatingDriver() {
    return (
      this.updateFormulaDriverMut.getCurrentResult().status === 'pending' ||
      this.updateFreestyleDriverMut.getCurrentResult().status === 'pending'
    );
  }

  protected get newDriverId() {
    return (
      this.createFormulaDriverMut.getCurrentResult().data?.id ??
      this.createFreestyleDriverMut.getCurrentResult().data?.id
    );
  }

  protected get updatedDriverId() {
    return (
      this.updateFormulaDriverMut.getCurrentResult().data?.id ??
      this.updateFreestyleDriverMut.getCurrentResult().data?.id
    );
  }

  protected updateDependentQueries() {
    if (!this.driverId || this.loadedDriverId === this.driverId || !this.driversOrder) {
      return;
    }

    const driverNode = this.driversOrder.find(node => node.node_id === this.driverId);

    if (!driverNode) {
      return;
    }

    if (driverNode.driver_type === NonFinancialDriverType.FORMULA) {
      this.formulaDriverDetailObs.setOptions(
        formulaDriverQueryConfig(this.workspaceId, this.organisationId, this.driverId)
      );
    } else if (driverNode.driver_type === NonFinancialDriverType.FREESTYLE) {
      this.freestyleDriverDetailObs.setOptions(
        freestyleDriverQueryConfig(this.workspaceId, this.organisationId, this.driverId)
      );
    }

    this.loadedDriverId = this.driverId;
  }
}
